<script setup lang="ts">
import {computed, ref, useId} from "vue";
import {watchDebounced} from "@vueuse/core";
import {MessageSquareText, Smartphone, TriangleAlert} from "@lucide/vue";
import {Checkbox} from "@/core/components/ui/checkbox";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {Skeleton} from "@/core/components/ui/skeleton";
import {appointmentApi} from "@/container";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {PatientNotificationEvent, PatientNotificationPreview} from "@/core/http/resources/AppointmentResource";
import {isPatientSmsChoiceValid, PatientSmsChoice} from "@/agenda/lib/patientSms";

const props = defineProps<{
    /** The appointment cancelled or moved. None for a creation, previewed from its draft. */
    appointmentId?: number | null;
    /** The patient of the appointment being created. */
    customerId?: number | null;
    event: PatientNotificationEvent;
    /** The start of a creation or the new start of a move, in the format sent to the API. */
    startAt?: string | null;
    /** Drawn without its own frame, for a form that already sets the option apart. */
    bare?: boolean;
}>();

const choice = defineModel<PatientSmsChoice>({required: true});

const organizationId = useOrganizationId();

const preview = ref<PatientNotificationPreview | null>(null);
const previewFailed = ref(false);
const previewLoading = ref(false);
let latestRequest = 0;
let prefilled = false;

const checkboxId = useId();
const phoneId = useId();

const LABELS: Record<PatientNotificationEvent, string> = {
    created: 'Envoyer un SMS de confirmation au patient',
    canceled: 'Prévenir le patient de l\'annulation par SMS',
    rescheduled: 'Prévenir le patient du nouvel horaire par SMS'
};

const label = computed(() => LABELS[props.event]);

const phoneInvalid = computed(() => !isPatientSmsChoiceValid(choice.value));

const setNotify = (notify: boolean | 'indeterminate') => {
    choice.value = {...choice.value, notify: notify === true};
};

const setPhone = (phone: string | number) => {
    choice.value = {...choice.value, phone: String(phone)};
};

/**
 * A creation is previewed from its draft, a change from the appointment
 * itself. Null while the draft still misses what the text names.
 */
const requestPreview = (): Promise<PatientNotificationPreview> | null => {
    if (props.event === 'created') {
        return organizationId.value && props.customerId && props.startAt
            ? appointmentApi().previewCreationNotification(organizationId.value, props.customerId, props.startAt)
            : null;
    }

    return props.appointmentId
        ? appointmentApi().previewPatientNotification(props.appointmentId, props.event, props.startAt ?? undefined)
        : null;
};

/**
 * Composed by the server, so the pharmacist reads exactly what will leave.
 * Fetched as soon as the option shows rather than on ticking: the block then
 * unfolds once, instead of growing again when the answer lands. Debounced for
 * the edit form, whose start changes with every keystroke of the time field.
 */
watchDebounced(() => [props.appointmentId, props.customerId, props.startAt] as const, async () => {
    const request = ++latestRequest;
    const pending = requestPreview();

    if (!pending) {
        previewLoading.value = false;

        return;
    }

    previewLoading.value = true;
    previewFailed.value = false;

    try {
        const loaded = await pending;

        if (request !== latestRequest) {
            return;
        }

        preview.value = loaded;

        // Once only: a number the pharmacist cleared must stay cleared when
        // the preview is reloaded for another time.
        if (!prefilled && !choice.value.phone && loaded.phone) {
            setPhone(loaded.phone);
        }

        prefilled = true;
    } catch {
        if (request === latestRequest) {
            preview.value = null;
            previewFailed.value = true;
        }
    } finally {
        if (request === latestRequest) {
            previewLoading.value = false;
        }
    }
}, {immediate: true, debounce: 300});
</script>

<template>
  <div class="flex flex-col gap-3 text-sm" :class="{'rounded-md border border-gray-200 px-3 py-2.5': !bare}">
    <div class="flex items-start gap-2">
      <Checkbox
        :id="checkboxId"
        :model-value="choice.notify"
        class="mt-0.5"
        @update:model-value="setNotify"
      />
      <div class="flex flex-col gap-0.5">
        <Label :for="checkboxId" class="leading-snug">
          {{ label }}
        </Label>
        <span class="text-xs text-gray-500">
          Facultatif : sans cette case, aucun SMS n'est envoyé.
        </span>
      </div>
    </div>
    <template v-if="choice.notify">
      <Skeleton v-if="previewLoading && !preview" class="h-4 w-48" />
      <p v-else-if="preview?.phone" class="flex items-center gap-1.5 text-xs text-gray-500">
        <Smartphone class="size-3.5 shrink-0" />
        Mobile du dossier : {{ preview.phone }}
      </p>
      <p
        v-else-if="preview"
        class="flex items-center gap-1.5 rounded-md bg-orange/20 px-2 py-1.5 text-xs text-gray-dark"
      >
        <TriangleAlert class="size-3.5 shrink-0" />
        Aucun mobile connu : saisissez celui du patient.
      </p>
      <div class="flex flex-col gap-1.5">
        <Label :for="phoneId">Numéro du destinataire</Label>
        <Input
          :id="phoneId"
          :model-value="choice.phone"
          type="tel"
          placeholder="06 12 34 56 78"
          @update:model-value="setPhone"
        />
        <span v-if="phoneInvalid && choice.phone" class="text-xs text-red-500">
          Saisissez un mobile français (06 ou 07).
        </span>
      </div>
      <div class="flex flex-col gap-1.5">
        <span class="flex items-center gap-1.5 text-xs font-medium text-gray-500">
          <MessageSquareText class="size-3.5 shrink-0" />
          Aperçu du SMS
        </span>
        <p v-if="previewFailed" class="text-xs text-gray-400">
          Aperçu indisponible.
        </p>
        <Skeleton v-else-if="!preview" class="h-16 w-full" />
        <p v-else class="rounded-md bg-primary-lightest px-3 py-2 text-gray-dark" :class="{'opacity-60': previewLoading}">
          {{ preview.content }}
        </p>
      </div>
    </template>
  </div>
</template>
